import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';

const CANONICAL_BASE = 'https://www.kidsevent-schwerin.de';

const SEO = ({
  page = 'home',
  title: overrideTitle,
  description: overrideDesc,
  keywords: overrideKw,
  image: overrideImage,
  type = 'website',
  noindex = false,
  faqs = null,
}) => {
  const { settings } = useSettings();
  const location = useLocation();
  const seo = settings?.seo?.[page] || {};
  const brand = settings?.brand || {};
  const contact = settings?.contact || {};

  const siteName = brand.site_name || 'Hüpfburgen Verleih Schwerin';
  const baseTitle = overrideTitle || seo.title || `${siteName} – Hüpfburgen mieten`;
  // Global title suffix from settings (e.g. "KidsEvent Schwerin"). Applied to every page
  // title automatically – unless the title already ends with it (avoids double-append on
  // pages that hardcode the brand in their own SEO setup).
  const suffix = (settings?.seo?.title_suffix || '').trim();
  const separator = (settings?.seo?.title_separator || '–').trim() || '–';
  const titleHasSuffix = suffix && new RegExp(`(^|\\s)${suffix.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\s*$`, 'i').test(baseTitle);
  const title = suffix && !titleHasSuffix ? `${baseTitle} ${separator} ${suffix}` : baseTitle;
  const description = overrideDesc || seo.description || 'Hüpfburgen mieten in Schwerin, Westmecklenburg und Lübeck.';
  const keywords = overrideKw || seo.keywords || '';

  // Determine canonical URL
  const seoBase = (settings?.seo?.canonical_url || CANONICAL_BASE).replace(/\/$/, '');
  const canonicalUrl = `${seoBase}${location.pathname}`;

  // Resolve image (absolute URL preferred for OG)
  let imageUrl = overrideImage || seo.og_image || brand.logo_url;
  if (imageUrl && imageUrl.startsWith('/')) {
    imageUrl = `${seoBase}${imageUrl}`;
  }

  // Parse opening hours from settings (string like "Mo-Fr 09:00-18:00, Sa 10:00-14:00")
  // and turn it into Schema.org openingHoursSpecification array.
  const parseHours = (raw) => {
    if (!raw || typeof raw !== 'string') return undefined;
    const dayMap = { mo: 'Monday', di: 'Tuesday', mi: 'Wednesday', do: 'Thursday',
      fr: 'Friday', sa: 'Saturday', so: 'Sunday' };
    const out = [];
    raw.split(/[,;]+/).forEach((chunk) => {
      const m = chunk.trim().toLowerCase().match(/^([a-zäö]{2})\s*(?:-\s*([a-zäö]{2}))?\s+(\d{1,2}[:.]\d{2})\s*-\s*(\d{1,2}[:.]\d{2})/);
      if (!m) return;
      const from = dayMap[m[1]] || null;
      const to = m[2] ? (dayMap[m[2]] || null) : null;
      if (!from) return;
      const days = [];
      if (to) {
        const order = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
        const i1 = order.indexOf(from);
        const i2 = order.indexOf(to);
        if (i1 >= 0 && i2 >= i1) days.push(...order.slice(i1, i2 + 1));
        else days.push(from);
      } else {
        days.push(from);
      }
      out.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: days,
        opens: m[3].replace('.', ':'),
        closes: m[4].replace('.', ':'),
      });
    });
    return out.length ? out : undefined;
  };

  // LocalBusiness / Organization JSON-LD
  const localBusinessLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${seoBase}/#localbusiness`,
    name: siteName,
    url: seoBase,
    image: imageUrl,
    description,
    telephone: contact.phone || undefined,
    email: contact.email || undefined,
    address: contact.address
      ? {
          '@type': 'PostalAddress',
          streetAddress: contact.address,
          postalCode: contact.zip_code || contact.zip || undefined,
          addressLocality: contact.city || 'Schwerin',
          addressRegion: 'Mecklenburg-Vorpommern',
          addressCountry: 'DE',
        }
      : undefined,
    sameAs: [
      contact.facebook || undefined,
      contact.instagram || undefined,
      contact.tiktok || undefined,
      contact.youtube || undefined,
    ].filter(Boolean),
    priceRange: '€€',
    areaServed: ['Schwerin', 'Westmecklenburg', 'Lübeck'],
    openingHoursSpecification: parseHours(contact.opening_hours || contact.business_hours) || [
      // Sensible fallback so Google has structured data even if the admin hasn't filled the field.
      { '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'],
        opens: '09:00', closes: '18:00' },
      { '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
    ],
    aggregateRating: (() => {
      const r = settings?.reviews_summary || settings?.reviews || {};
      const rating = r.rating ?? r.aggregate_rating;
      const count  = r.review_count ?? r.user_ratings_total ?? r.count;
      if (rating && count) {
        return {
          '@type': 'AggregateRating',
          ratingValue: Number(rating).toFixed(1),
          reviewCount: Number(count),
          bestRating: '5',
          worstRating: '1',
        };
      }
      return undefined;
    })(),
  };

  // FAQ schema (optional)
  const faqLd = (Array.isArray(faqs) && faqs.length > 0) ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs
      .filter((f) => f && f.q && f.a)
      .map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
  } : null;

  return (
    <Helmet>
      <html lang="de" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* LocalBusiness structured data */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessLd)}
      </script>
      {faqLd && (
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
